import { Button, message } from 'antd';
import HmsEntityTableController from 'components/HmsEntityTableController';
import useEntities from 'hooks/useEntities';
import useOrganization from 'hooks/useOrganization';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useLoginStore from 'store/loginStore';
import useRequiredActionsStore from 'store/requiredActionsStore';
import { initiatePayment, patchEntity } from 'utils/apiFunctions';
import PaymentStatusModal from '../components/Modals/PaymentStatusModal';
import SeasonPlayerRegistrationModal from '../components/Modals/SeasonPlayerRegistrationModal';

const SeasonPlayerRegistrationsPlayer = () => {
  const entities = useEntities();
  const entity = entities.SeasonPlayerRegistration;
  const { organization } = useOrganization();
  const organizationId = organization?.organizationId;
  const tableRef = useRef();
  const [modal, setModal] = useState({ open: false });
  const currentRole = useLoginStore(s => s.getCurrentRole)();
  const [step, setStep] = useState(1);
  const [agree, setAgree] = useState(false);
  const [payOption, setPayOption] = useState('manager');
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [termsChecked, setTermsChecked] = useState([]);
  const user = useLoginStore(s => s.user);
  const player = user?.Players?.[0];
  const currentPlayerStatus = player?.status;
  // HMS-76: hrac cekajici na schvaleni (PREAPPROVED) se muze registrovat do souteze a zaplatit, jen do Lineupu nesmi
  const isPlayerActive = currentPlayerStatus === 'ACTIVE' || (organization?.playerApprovalRequired && currentPlayerStatus === 'PREAPPROVED');
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentStatus = searchParams.get('paymentStatus');
  const paymentStatusModal = { open: paymentStatus !== null, status: paymentStatus };
  const reloadRequiredActions = useRequiredActionsStore(s => s.reload);

  function closeModal() {
    // needs to be done like this, otherwise terms will blink for a moment while closing (due to all properties begin erased)
    setModal(p => ({ ...p, open: false }));
  }

  function openModal(options) {
    setModal({
      open: true,
      ...options,
    });
  }

  const renderActionColumn = (record) => {
    const season = record.Season;
    if (!season) return null;
    const needsPayment = !!season.playerRegFeeMandatory;

    function AcceptAndPayButton({ title, type, step = 1 }) {
      const Element = ({ children, ...props }) => {
        if (type === 'link') {
          return <a {...props}>{children}</a>;
        }
        return <Button {...props}>{children}</Button>;
      };

      return (
        <Element
          type="primary"
          size="small"
          onClick={e => {
            e.stopPropagation();
            setCurrentRecord(record);
            setStep(step);
            setAgree(false);
            setPayOption('online');
            openModal({
              needsPayment,
            });
          }}
        >
          {title}
        </Element>
      );
    }

    if (!record.isAccepted) {
      return (
        <AcceptAndPayButton title={needsPayment ? 'Accept and pay...' : 'Accept...'} />
      )
    } else {
      if (record.isConfirmed) {
        return (
          <span>
            ✅ No further action needed.
          </span>
        )
      } else {
        if (needsPayment) {
          if (!record.isPaid) {
            switch (record.paymentMethod) {
              case 'CASH':
                return (
                  <div className="flex flex-col gap-2">
                    <div>
                      💵 Please pay to your team manager.
                    </div>
                    <div>
                      If you changed your mind, you can also <AcceptAndPayButton title="Pay on-line" type="link" step={2} />.
                    </div>
                  </div>
                )
              case 'COMGATE':
                return (
                  <span>
                    💵 We are waiting your payment. If you want to retry, please <AcceptAndPayButton title="Click here" />
                  </span>
                )
              default:
                console.log(record);
                return (
                  <span>
                    ⚠️ You're payment method is invalid. Please contact your team manager.
                  </span>
                )
            }
          }
        } else {
          return (
            <span>
              ⚠️ You're registration is paid, but is not confirmed. Please contact your team manager.
            </span>
          )
        }
      }
    }
  };

  const filterGroups = null;
  const showColumns = [
    'Competition.Name',
    'Season.Name',
    'Season.ParticipationFee',
    'Player_AcceptedByPlayer',
    'Player_Paid',
    'Player_Confirmed',
    {
      title: 'Action',
      key: 'action',
      width: 180,
      requiredAttributes: ['isAccepted', 'isPaid', 'paymentMethod'],
      requiredRelations: ['Season(playerRegFeeMandatory)'],
      render: (record) => renderActionColumn(record),
    },
  ];

  const renderMobileRow = (record) => {
    return (
      <div className="p-3 border rounded bg-white shadow-sm flex flex-col gap-3 cursor-default">
        <div className="text-sm text-gray-600 flex flex-wrap gap-2 items-center">
          <span>{entity.columns['Competition.Name'].render(record)}</span>
          <span>•</span>
          <span>{entity.columns['Season.Name'].render(record)}</span>
        </div>
        <div className="flex grid grid-cols-[auto_1fr] gap-2 gap-x-4">
          <span>Accepted by Player:</span>
          <span>{entity.columns['Player_AcceptedByPlayer'].render(record)}</span>
          <span>Paid:</span>
          <span>{entity.columns['Player_Paid'].render(record)}</span>
          <span>Confirmed:</span>
          <span>{entity.columns['Player_Confirmed'].render(record)}</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          {renderActionColumn(record)}
        </div>
      </div>
    );
  };

  const handleAccept = async (option) => {
    setLoading(true);
    try {
      if (!modal.needsPayment) {
        await patchEntity(organizationId, entity, currentRecord.seasonPlayerRegistrationId, { isAccepted: true, isConfirmed: true }, currentRole)
          .then((response) => {
            if (response.status !== 200) {
              message.error('Failed to update registration.');
              return;
            }
            message.success('Registration updated');
            closeModal();
            tableRef.current?.reload();
            reloadRequiredActions();
          })
          .catch(() => {
            message.error('Failed to update registration.');
          });
      } else if (option === 'manager') {
        await patchEntity(organizationId, entity, currentRecord.seasonPlayerRegistrationId,
          {
            isAccepted: true,
            paymentMethod: 'CASH'
          }, currentRole)
          .then((response) => {
            if (response.status !== 200) {
              message.error('Failed to update registration.');
              return;
            }
            message.success('Registration updated');
            closeModal();
            tableRef.current?.reload();
            reloadRequiredActions();
          })
          .catch(() => {
            message.error('Failed to update registration.');
          });
      } else if (option === 'online') {
        await patchEntity(organizationId, entity, currentRecord.seasonPlayerRegistrationId,
          {
            isAccepted: true,
            paymentMethod: 'COMGATE'
          }, currentRole)
          .then(async (response) => {
            if (response.status !== 200) {
              message.error('Failed to update registration.');
              return;
            }
            closeModal();
            tableRef.current?.reload();

            message.success('Redirecting for payment...');
            closeModal();
            tableRef.current?.reload();

            const paymentResp = await initiatePayment({
              seasonPlayerRegistrationId: currentRecord.seasonPlayerRegistrationId,
            }, currentRole)
            if (paymentResp.status !== 200) {
              message.error('Failed to initiate payment.');
              return;
            }
            const paymentRespJson = await paymentResp.json();

            reloadRequiredActions();

            window.location.href = paymentRespJson.redirectTo;
          })
          .catch(() => {
            message.error('Failed to update registration.');
          });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isPlayerActive) {
    return null;
  }

  if (!organization?.seasonPlayerRegistrationEnabled) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold mb-4">Player Season Registration</h2>
        <p className="text-gray-600">
          Player season registration is currently disabled for this organization.
        </p>
      </div>
    );
  }

  return (
    <>
      <HmsEntityTableController
        ref={tableRef}
        entity={entity}
        filterGroups={filterGroups}
        columnSets={[
          {
            name: 'Current Season',
            columns: showColumns,
            filters: { SeasonStatus: ['OPEN_FOR_REGISTRATION', 'RUNNING'] },
          },
          {
            name: 'Archive',
            columns: showColumns,
            filters: { SeasonStatus: ['NOTSTARTED', 'FINISHED', 'POSTPONED', 'CANCELLED'] },
          },
        ]}
        canCreate={false}
        onRowClickLink={() => undefined}
        renderMobileRow={renderMobileRow}
        exportEnabled={false}
        columnSelectorEnabled={false}
        filterEnabled={false}
        searchEnabled={false}
      />
      <SeasonPlayerRegistrationModal
        open={modal.open}
        onCancel={closeModal}
        onAccept={handleAccept}
        loading={loading}
        modal={modal}
        step={step}
        setStep={setStep}
        agree={agree}
        setAgree={setAgree}
        payOption={payOption}
        setPayOption={setPayOption}
        currentRecord={currentRecord}
        termsChecked={termsChecked}
        setTermsChecked={setTermsChecked}
      />
      <PaymentStatusModal
        open={paymentStatusModal.open}
        onClose={() => {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('paymentStatus');
          setSearchParams(newSearchParams, { replace: true });
        }}
        paymentStatus={paymentStatusModal.status}
      />
    </>
  );
}

export default SeasonPlayerRegistrationsPlayer; 